import {
  Component, Input, HostListener, OnChanges, OnDestroy, SimpleChanges,
  Renderer2, ElementRef
} from '@angular/core';

export interface CarouselImage {
  thumb: string;
  full: string;
}

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.css']
})
export class CarouselComponent implements OnChanges, OnDestroy {
  @Input() images: CarouselImage[] = [];
  @Input() visibleCount = 1;
  @Input() titulo = '';
  @Input() texto1 = '';
  @Input() texto2 = '';

  currentIndex = 0;
  popupIndex: number | null = null;
  private touchStartX = 0;
  private modalTouchStartX = 0;
  private triggerElement: HTMLElement | null = null;
  private previousOverflow: string | null = null;

  constructor(private renderer: Renderer2, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['images'] || changes['visibleCount']) {
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      if (this.popupIndex !== null && this.popupIndex >= this.images.length) this.closePopup();
    }
  }

  get count(): number {
    const n = Number(this.visibleCount);
    return Number.isFinite(n) ? Math.max(1, Math.min(Math.floor(n), Math.max(this.images.length, 1))) : 1;
  }

  get slideWidth(): string { return `${100 / this.count}%`; }
  get maxIndex(): number { return Math.max(0, this.images.length - this.count); }
  get totalPages(): number { return this.maxIndex + 1; }
  get canNavigate(): boolean { return this.maxIndex > 0; }
  get selectedImage(): CarouselImage | null {
    return this.popupIndex === null ? null : (this.images[this.popupIndex] || null);
  }

  next(): void {
    if (this.canNavigate) this.currentIndex = this.currentIndex >= this.maxIndex ? 0 : this.currentIndex + 1;
  }
  prev(): void {
    if (this.canNavigate) this.currentIndex = this.currentIndex <= 0 ? this.maxIndex : this.currentIndex - 1;
  }
  goTo(index: number): void {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
  }

  openPopup(index: number, event?: Event): void {
    if (!this.images[index]) return;
    this.triggerElement = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.popupIndex = index;
    if (this.previousOverflow === null) {
      this.previousOverflow = document.body.style.overflow;
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    }
  }

  closePopup(): void {
    if (this.popupIndex === null) return;
    this.popupIndex = null;
    if (this.previousOverflow !== null) {
      this.renderer.setStyle(document.body, 'overflow', this.previousOverflow);
      this.previousOverflow = null;
    }
    this.triggerElement?.focus();
    this.triggerElement = null;
  }

  popupNext(): void {
    if (this.popupIndex !== null && this.images.length > 1) {
      this.popupIndex = (this.popupIndex + 1) % this.images.length;
    }
  }
  popupPrev(): void {
    if (this.popupIndex !== null && this.images.length > 1) {
      this.popupIndex = (this.popupIndex - 1 + this.images.length) % this.images.length;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.popupIndex !== null) {
      if (event.key === 'Escape') { event.preventDefault(); this.closePopup(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); this.popupNext(); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); this.popupPrev(); }
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target || !this.host.nativeElement.contains(target) ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.prev(); }
  }

  onTouchStart(event: TouchEvent): void { this.touchStartX = event.changedTouches[0].screenX; }
  onTouchEnd(event: TouchEvent): void {
    const delta = event.changedTouches[0].screenX - this.touchStartX;
    if (Math.abs(delta) < 45) return;
    delta < 0 ? this.next() : this.prev();
  }
  onModalTouchStart(event: TouchEvent): void { this.modalTouchStartX = event.changedTouches[0].screenX; }
  onModalTouchEnd(event: TouchEvent): void {
    const delta = event.changedTouches[0].screenX - this.modalTouchStartX;
    if (Math.abs(delta) < 55) return;
    delta < 0 ? this.popupNext() : this.popupPrev();
  }

  trackByIndex(index: number): number { return index; }
  ngOnDestroy(): void {
    if (this.previousOverflow !== null) {
      this.renderer.setStyle(document.body, 'overflow', this.previousOverflow);
      this.previousOverflow = null;
    }
  }
}
